import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { api } from '../services/api';
import { Card, Button, Spinner, Alert } from '../components/ui';
import { Instagram, Facebook, X } from 'lucide-react';
import { parseISO, format } from 'date-fns';
import { getRedirectUrl } from '../utils/helpers';

const SocialsPage = () => {
  const { accessKey } = useAppStore();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  // Fetch social accounts
  const {
    data: accounts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['social-accounts'],
    queryFn: () => api.fetchSocialAccounts(accessKey),
    enabled: !!accessKey,
  });

  // Connect Instagram
  const connectInstagram = async () => {
    setLoading(true);

    await api.connectInstagramAccount();

    setLoading(false);
  };

  // Connect Facebook
  const connectFacebook = async () => {
    setLoading(true);

    await api.connectFacebookAccount();
  };

  // Disconnect account
  const disconnectMutation = useMutation({
    mutationFn: ({ accountId, platform }) => {
      return api.disconnectAccount(accountId, platform);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
    },
  });

  const handleDisconnect = (accountId, platform) => {
    if (window.confirm(`Are you sure you want to disconnect this ${platform} account?`)) {
      disconnectMutation.mutate({ accountId, platform });
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Spinner size='lg' />
      </div>
    );
  }

  if (error) {
    return <Alert variant='error'>Failed to load social accounts: {error.message}</Alert>;
  }

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Onboarding Section */}
        <Card>
          <Card.Body>
            <h2 className='text-lg font-semibold mb-4'>Connect Social Accounts</h2>
            <p className='text-gray-600 mb-6'>
              Connect your social media accounts to manage and schedule content across platforms.
            </p>

            {/* Instagram Business Section */}
            <div className='border border-gray-200 rounded-lg p-4 mb-4'>
              <div className='flex items-center mb-3'>
                <div className='w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3'>
                  <Instagram className='w-5 h-5 text-white' />
                </div>
                <div>
                  <h3 className='font-medium text-gray-900'>Instagram Business</h3>
                  <p className='text-sm text-gray-600'>Connect your Instagram Business account</p>
                </div>
              </div>
              <Button
                onClick={connectInstagram}
                disabled={accounts.find((account) => account.platform === 'instagram_business')}
                className='w-full'
              >
                {accounts.find((account) => account.platform === 'instagram_business')
                  ? 'Connected'
                  : 'Connect Instagram'}
              </Button>
            </div>

            {/* Facebook Section */}
            <div className='border border-gray-200 rounded-lg p-4'>
              <div className='flex items-center mb-3'>
                <div className='w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3'>
                  <Facebook className='w-5 h-5 text-white' />
                </div>
                <div>
                  <h3 className='font-medium text-gray-900'>Facebook</h3>
                  <p className='text-sm text-gray-600'>Connect your Facebook page</p>
                </div>
              </div>
              <Button
                onClick={connectFacebook}
                disabled={accounts.find(
                  (account) => account.platform === 'facebook' || account.platform === 'instagram'
                )}
                className='w-full'
              >
                {accounts.find(
                  (account) => account.platform === 'facebook' || account.platform === 'instagram'
                )
                  ? 'Connected'
                  : 'Connect Facebook'}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Connected Accounts Section */}
        <Card>
          <Card.Body>
            <h2 className='text-lg font-semibold mb-4'>Connected Accounts</h2>
            <p className='text-gray-600 mb-6'>Manage your connected social media accounts.</p>

            {accounts.length === 0 ? (
              <div className='text-center py-12'>
                <div className='text-4xl mb-4'>📱</div>
                <h3 className='text-lg font-medium text-gray-900 mb-2'>No accounts connected</h3>
                <p className='text-gray-600'>Connect your social media accounts to get started.</p>
              </div>
            ) : (
              <div className='space-y-3'>
                {accounts.map((account) => (
                  <div
                    key={account.id}
                    className={`border border-gray-200 rounded-lg p-4 flex items-center ${
                      account.status !== 'active' ? 'opacity-70 bg-gray-50' : ''
                    }`}
                  >
                    <div className='w-12 h-12 rounded-full overflow-hidden mr-4 bg-gray-100 flex items-center justify-center'>
                      {account.profile_photo ? (
                        <img
                          src={account.profile_photo}
                          alt='Profile'
                          className='w-full h-full object-cover'
                        />
                      ) : (
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            account.platform === 'instagram'
                              ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                              : 'bg-blue-600'
                          }`}
                        >
                          {account.platform === 'instagram' ? (
                            <Instagram className='w-4 h-4 text-white' />
                          ) : (
                            <Facebook className='w-4 h-4 text-white' />
                          )}
                        </div>
                      )}
                    </div>

                    <div className='flex-1'>
                      <h4 className='font-medium text-gray-900'>{account.username}</h4>
                      <div className='flex items-center gap-2 mt-1'>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            account.platform === 'instagram'
                              ? 'bg-pink-100 text-pink-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {account.platform}
                        </span>
                        {account.account_type && (
                          <span className='px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800'>
                            {account.account_type}
                          </span>
                        )}
                        {account.status !== 'active' && (
                          <span className='px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800'>
                            {account.status}
                          </span>
                        )}
                      </div>
                      <p className='text-xs text-gray-500 mt-1'>
                        Connected on {format(parseISO(account.connected_at), 'MMM d, yyyy')}
                      </p>
                    </div>

                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => handleDisconnect(account.id, account.platform)}
                      disabled={account.status !== 'active'}
                    >
                      <X className='w-4 h-4 mr-1' />
                      Disconnect
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default SocialsPage;
